import { MOTIVOS_BASURA, type CalidadLead, type LeadCalificarInput, type MotivoBasura } from '@app/shared';
import { Target, ThumbsDown, ThumbsUp, Trophy, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

interface Props {
  calidad: CalidadLead;
  motivoBasura: MotivoBasura | null;
  guardando: boolean;
  onCalificar: (input: LeadCalificarInput) => void;
}

// Cómo se ve el ícono según la calidad: vacío y punteado mientras falta
// calificar, lleno con el color de la calidad cuando ya está.
const ASPECTO: Record<Exclude<CalidadLead, 'SIN_CALIFICAR'>, { icono: LucideIcon; clase: string }> = {
  BUENO: { icono: ThumbsUp, clase: 'border-success bg-success text-white' },
  BASURA: { icono: ThumbsDown, clase: 'border-danger bg-danger text-white' },
  COMPRO: { icono: Trophy, clase: 'border-warning bg-warning text-white' },
};

const OPCIONES = ['BUENO', 'BASURA', 'COMPRO'] as const;

// ciclo-calidad-leads (R6, pedido de Javier el 15/10): calificar el lead para
// mejorar los leads de Meta. Un ícono chico y bien distinto en la cabecera de
// la charla, separado de los filtros y del menú de cerrar; al tocarlo, un
// pop-up mínimo. Los motivos solo se habilitan si se elige Basura, y Basura se
// guarda recién al elegir el motivo.
export function CalificarLead({ calidad, motivoBasura, guardando, onCalificar }: Props): JSX.Element {
  const es = useI18n();
  const t = es.bandeja.calidad;
  const [abierto, setAbierto] = useState(false);
  const [eligiendoBasura, setEligiendoBasura] = useState(false);
  const contenedor = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    function afuera(e: MouseEvent): void {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setAbierto(false);
    }
    function escape(e: KeyboardEvent): void {
      if (e.key === 'Escape') setAbierto(false);
    }
    window.addEventListener('mousedown', afuera);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('mousedown', afuera);
      window.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const abrir = (): void => {
    setEligiendoBasura(calidad === 'BASURA');
    setAbierto((v) => !v);
  };

  const guardar = (input: LeadCalificarInput): void => {
    onCalificar(input);
    setAbierto(false);
  };

  const motivosHabilitados = eligiendoBasura;
  const aspecto = calidad === 'SIN_CALIFICAR' ? null : ASPECTO[calidad];
  const Icono = aspecto?.icono ?? Target;

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        onClick={abrir}
        aria-label={`${t.abrir}: ${t.opciones[calidad]}`}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        title={t.abrir}
        data-calidad={calidad}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
          aspecto
            ? aspecto.clase
            : 'border-dashed border-marca-primario text-marca-primario hover:bg-marca-primario/10',
        )}
      >
        <Icono size={15} />
      </button>

      {abierto && (
        <div
          role="dialog"
          aria-label={t.abrir}
          className="absolute right-0 top-full z-30 mt-2 w-64 rounded-md border border-border-subtle bg-surface p-3 shadow-lg"
        >
          <p className="text-xs text-text-subtle">{t.explicacion}</p>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {OPCIONES.map((op) => {
              const { icono: IconoOp } = ASPECTO[op];
              const activa = op === 'BASURA' ? eligiendoBasura : calidad === op && !eligiendoBasura;
              return (
                <button
                  key={op}
                  type="button"
                  disabled={guardando}
                  aria-pressed={activa}
                  onClick={() => (op === 'BASURA' ? setEligiendoBasura(true) : guardar({ calidad: op }))}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md border px-1 py-2 text-xs font-medium',
                    activa
                      ? ASPECTO[op].clase
                      : 'border-border-strong text-text-strong hover:bg-surface-muted',
                  )}
                >
                  <IconoOp size={14} />
                  {t.opciones[op]}
                </button>
              );
            })}
          </div>

          <fieldset disabled={!motivosHabilitados || guardando} className="mt-3">
            <legend
              className={cn(
                'text-xs font-medium',
                motivosHabilitados ? 'text-text-strong' : 'text-text-faint',
              )}
            >
              {t.porQue}
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {MOTIVOS_BASURA.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={calidad === 'BASURA' && motivoBasura === m}
                  onClick={() => guardar({ calidad: 'BASURA', motivoBasura: m })}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[11px]',
                    calidad === 'BASURA' && motivoBasura === m
                      ? 'border-danger bg-danger text-white'
                      : 'border-border-strong text-text-strong enabled:hover:bg-surface-muted',
                    'disabled:cursor-not-allowed disabled:opacity-40',
                  )}
                >
                  {t.motivos[m]}
                </button>
              ))}
            </div>
          </fieldset>

          {calidad !== 'SIN_CALIFICAR' && (
            <button
              type="button"
              disabled={guardando}
              onClick={() => guardar({ calidad: 'SIN_CALIFICAR' })}
              className="mt-3 text-[11px] text-text-subtle underline hover:text-text-strong"
            >
              {t.deshacer}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
